import { useState } from 'react'
import log1 from '../../assets/images/log_1.png'
import log2 from '../../assets/images/log_2.png'
import log3 from '../../assets/images/log_3.png'
import log4 from '../../assets/images/log_4.png'
import log5 from '../../assets/images/log_5.png'
import log6 from '../../assets/images/log_6.png'
import arrowRight from '../../assets/images/arrow-right-white.svg'

interface Industry {
  title: string
  description: string
  images: string[]
}

const industries: Industry[] = [
  {
    title: "RETAIL & ECOMMERCE",
    description: "Deliver delight, not delays. Whether you're selling fashion, gadgets, homeware, or cosmetics, Speedyssey helps you fulfill customer expectations with fast, trackable deliveries and flexible pickup scheduling.",
    images: [log1, log2, log3, log4, log5, log6]
  },
  {
    title: "FOOD & BEVERAGE",
    description: "Freshness is everything. Our delivery network is optimized for temperature-sensitive and time-critical food items — from restaurants and cloud kitchens to meal subscriptions and grocery kits.",
    images: [log1, log2, log3, log4, log5, log6]
  },
  {
    title: "CORPORATE DELIVERIES",
    description: "Move smarter across departments or locations. From daily document dispatches to office supply drops, we offer secure, professional handling with full transparency.",
    images: [log1, log2, log3, log4, log5, log6]
  },
  {
    title: "MEDICAL LOGISTICS",
    description: "When it comes to health, speed and care are non-negotiable. We support pharmacies, labs, and clinics with fast, secure delivery of prescriptions, test samples, and medical supplies — all handled with precision and compliance in mind.",
    images: [log1, log2, log3, log4, log5, log6]
  },
  {
    title: "HOME SERVICES",
    description: "From personal care kits to DIY tools, Speedyssey helps home-based and small-scale entrepreneurs deliver straight to customers' doors. Pickup and drop-off options make it easy to operate without a storefront.",
    images: [log1, log2, log3, log4, log5, log6]
  }
]

function Logistics() {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)

  const nextSlide = () => {
    if (isAnimating) return
    setIsAnimating(true)
    setCurrentSlide((prev) => (prev + 1) % industries.length)
    setTimeout(() => setIsAnimating(false), 500)
  }

  const prevSlide = () => {
    if (isAnimating) return
    setIsAnimating(true)
    setCurrentSlide((prev) => (prev - 1 + industries.length) % industries.length)
    setTimeout(() => setIsAnimating(false), 500)
  }

  const goToSlide = (index: number) => {
    if (isAnimating || index === currentSlide) return
    setIsAnimating(true)
    setCurrentSlide(index)
    setTimeout(() => setIsAnimating(false), 500)
  }

  return (
    <section className="bg-[#191919] py-20 px-4">
      <div className="container sm:px-10 px-3  mx-auto">
        {/* Header Section */}
        <div className="text-center mb-16">
          <h2 
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight text-left" 
            style={{ fontFamily: 'Antonio, sans-serif' }}
          >
            FLEXIBLE DELIVERY SOLUTIONS<br />
            FOR <span className="text-[#F55109]">ALL INDUSTRIES</span>
          </h2>
          
          <p className="text-white text-lg max-w-4xl mb-8 leading-relaxed text-left">
            Whether you're delivering hot meals, medical kits, or retail goods, Speedyssey adapts to your business model and your customer flow.
          </p>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            <button 
              onClick={prevSlide}
              disabled={isAnimating}
              className="w-12 h-12 rounded-full border border-gray-600 hover:border-orange-500 flex items-center justify-center transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <img
                src={arrowRight}
                alt="Previous"
                className="w-6 h-6 rotate-180"
              />
            </button>

            <div className="flex gap-2.5">
              {industries.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  disabled={isAnimating}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                    index === currentSlide ? 'bg-orange-500 scale-125' : 'bg-gray-600 hover:bg-gray-500'
                  }`}
                />
              ))}
            </div>

            <button 
              onClick={nextSlide}
              disabled={isAnimating}
              className="w-12 h-12 rounded-full border border-gray-600 hover:border-orange-500 flex items-center justify-center transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <img
                src={arrowRight}
                alt="Next"
                className="w-6 h-6"
              />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="mb-12 overflow-hidden">
          <div 
            className="transition-all duration-500 ease-in-out"
            style={{
              transform: isAnimating ? 'translateY(10px)' : 'translateY(0)',
              opacity: isAnimating ? 0.7 : 1
            }}
          >
            <h3 
              className="text-2xl md:text-3xl font-bold text-white mb-6 uppercase tracking-wider"
              style={{ fontFamily: 'Antonio, sans-serif' }}
            >
              {industries[currentSlide].title}
            </h3>
            
            <p className="text-white text-lg leading-relaxed max-w-5xl">
              {industries[currentSlide].description}
            </p>
          </div>
        </div>
      </div>

      {/* Image Grid - Full Width */}
      <div className="relative -mx-4 overflow-hidden">
        <div 
          className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 transition-all duration-500 ease-in-out gap-2 sm:gap-4"
          style={{
            transform: isAnimating ? 'scale(0.98)' : 'scale(1)',
            opacity: isAnimating ? 0.8 : 1
          }}
        >
          {industries[currentSlide].images.map((image, index) => (
            <div 
              key={`${currentSlide}-${index}`}
              className="relative overflow-hidden group"
            >
              <img
                src={image}
                alt={`Delivery scenario ${index + 1}`}
                className="w-full h-32 sm:h-48 md:h-64 object-cover transition-all duration-500 ease-in-out group-hover:scale-105"
                style={{
                  transform: isAnimating ? 'scale(0.95)' : 'scale(1)',
                  opacity: isAnimating ? 0.8 : 1,
                  transition: 'all 0.5s ease-in-out'
                }}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default Logistics
